package com.gsb.vyapar.bhagya.presentation.navigation.view

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.gestures.detectTapGestures
import androidx.compose.foundation.gestures.detectTransformGestures
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.Surface
import androidx.compose.material.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.derivedStateOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateMapOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.setValue
import androidx.compose.runtime.snapshotFlow
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.ImageBitmap
import androidx.compose.ui.graphics.Path
import androidx.compose.ui.graphics.drawscope.rotate
import androidx.compose.ui.graphics.toComposeImageBitmap
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.layout.SubcomposeLayout
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextDecoration
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.Constraints
import androidx.compose.ui.unit.Density
import androidx.compose.ui.unit.IntOffset
import androidx.compose.ui.unit.IntSize
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.gsb.vyapar.bhagya.domain.Location
import com.gsb.vyapar.bhagya.domain.getDisplayName
import com.gsb.vyapar.bhagya.domain.getId
import com.gsb.vyapar.bhagya.domain.getName
import com.gsb.vyapar.bhagya.interactor.event.UiEvents
import com.gsb.vyapar.bhagya.interactor.uistate.UiState
import com.gsb.vyapar.bhagya.presentation.business.uistate.BusinessMapCameraPosition
import com.gsb.vyapar.bhagya.theme.primary
import com.gsb.vyapar.bhagya.theme.white
import kotlinx.browser.window
import kotlinx.coroutines.Job
import kotlinx.coroutines.await
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.filterNotNull
import kotlinx.coroutines.launch
import kotlin.collections.LinkedHashSet
import org.jetbrains.skia.Image
import org.khronos.webgl.Int8Array
import org.w3c.fetch.Response
import kotlin.math.PI
import kotlin.math.abs
import kotlin.math.atan
import kotlin.math.floor
import kotlin.math.ln
import kotlin.math.max
import kotlin.math.min
import kotlin.math.pow
import kotlin.math.roundToInt
import kotlin.math.sin
import kotlin.math.sqrt

private const val TILE_SIZE = 256.0
private const val MIN_ZOOM_LEVEL = 2
private const val MAX_ZOOM_LEVEL = 18
private const val DEFAULT_ZOOM_LEVEL = 14
private const val USER_LOCATION_ZOOM = 15
private const val BOUNDS_PADDING_FACTOR = 1.2
private const val MIN_SPAN = 1e-4
private const val POPUP_ANCHOR_DP = 16
private const val MARKER_RADIUS_DP = 12
private const val SELECTED_MARKER_RADIUS_DP = 16
private const val MARKER_TAP_TARGET_DP = 48
private const val MARKER_LABEL_WIDTH_DP = 140
private const val MARKER_LABEL_SPACING_DP = 8
private const val PAN_STEP_DP = 96
private const val INTERACTION_RESET_DELAY_MS = 750L
private const val DEFAULT_TILE_BACKGROUND = 0xFFE0E0E0
private const val DEFAULT_MAP_BACKGROUND = 0xFFF5F5F5
private const val MINIMUM_MARKER_ZOOM = 8f
private const val MINIMUM_ZOOM_FOR_CLEARANCE = 3.5f
private const val MARKER_HORIZONTAL_CLEARANCE_DP = 160f
private const val MARKER_VERTICAL_CLEARANCE_DP = 96f
private const val MIN_HORIZONTAL_CLEARANCE_DP = 112f
private const val MIN_VERTICAL_CLEARANCE_DP = 64f
private const val MAX_LOW_ZOOM_MARKERS = 6

private data class TileKey(val zoom: Int, val x: Int, val y: Int)

private data class JsMapState(
    val centerWorldX: Double,
    val centerWorldY: Double,
    val zoom: Int,
)

@Composable
actual fun PlatformBusinessMap(
    modifier: Modifier,
    businessLocations: List<BusinessLocationPin>,
    uiState: UiState,
    onTriggerEvent: (UiEvents) -> Unit,
    onInteractionChanged: (Boolean) -> Unit,
    enableUserLocation: Boolean,
    retainState: Boolean,
) {
    if (businessLocations.isEmpty()) {
        onInteractionChanged(false)
        return
    }

    val savedCamera = if (retainState) uiState.businessUiState.businessMapViewState.cameraPosition else null
    val savedSelectedId = if (retainState) uiState.businessUiState.businessMapViewState.selectedBusinessId else null

    val density = LocalDensity.current
    val markerRadiusPx = with(density) { MARKER_RADIUS_DP.dp.toPx() }
    val selectedMarkerRadiusPx = with(density) { SELECTED_MARKER_RADIUS_DP.dp.toPx() }
    val tapTargetRadiusPx = with(density) { MARKER_TAP_TARGET_DP.dp.toPx() }
    val anchorOffsetPx = with(density) { POPUP_ANCHOR_DP.dp.toPx() }
    val markerLabelWidthPx = with(density) { MARKER_LABEL_WIDTH_DP.dp.toPx() }
    val markerLabelSpacingPx = with(density) { MARKER_LABEL_SPACING_DP.dp.toPx() }
    val panStepPx = with(density) { PAN_STEP_DP.dp.toPx() }

    val tileCache = remember { mutableStateMapOf<TileKey, ImageBitmap?>() }
    val tileLoading = remember { mutableStateMapOf<TileKey, Boolean>() }
    val coroutineScope = rememberCoroutineScope()

    val fetchedBusinesses by uiState.businessUiState.isBusinessFetched.collectAsState()

    var mapSize by remember { mutableStateOf(IntSize.Zero) }
    var mapState by remember { mutableStateOf<JsMapState?>(null) }
    var selectedLocation by remember { mutableStateOf<BusinessLocationPin?>(null) }
    var shouldFitBounds by remember { mutableStateOf(!retainState || savedCamera == null) }
    var hasCenteredOnUser by remember { mutableStateOf(false) }
    var userLocation by remember {
        mutableStateOf(uiState.locationUiState.location?.takeIf { it.hasValidCoordinates() })
    }
    val canAutoCenterOnUser = enableUserLocation && (!retainState || savedCamera == null)

    val uiStateState = rememberUpdatedState(uiState)

    LaunchedEffect(retainState, savedSelectedId, businessLocations) {
        selectedLocation = savedSelectedId?.let { id ->
            businessLocations.firstOrNull { it.business.getId() == id }
        }
    }

    LaunchedEffect(mapSize, businessLocations, savedCamera, retainState) {
        if (mapSize == IntSize.Zero || businessLocations.isEmpty()) {
            return@LaunchedEffect
        }
        if (mapState == null) {
            val initialState = when {
                retainState && savedCamera != null -> savedCamera.toJsMapState(mapSize)
                else -> businessLocations.toInitialMapState(mapSize)
            }
            mapState = initialState
        }
    }

    LaunchedEffect(enableUserLocation) {
        if (!enableUserLocation) {
            userLocation = null
            hasCenteredOnUser = false
        }
    }

    LaunchedEffect(enableUserLocation, uiState.locationUiState.location) {
        if (!enableUserLocation) return@LaunchedEffect
        val location = uiState.locationUiState.location?.takeIf { it.hasValidCoordinates() }
        userLocation = location
        if (location == null) {
            hasCenteredOnUser = false
        }
    }

    LaunchedEffect(canAutoCenterOnUser, userLocation, mapState) {
        val state = mapState ?: return@LaunchedEffect
        val location = userLocation ?: return@LaunchedEffect
        if (canAutoCenterOnUser && !hasCenteredOnUser) {
            mapState = state.centerOn(location, USER_LOCATION_ZOOM)
            shouldFitBounds = false
            hasCenteredOnUser = true
        }
    }

    LaunchedEffect(businessLocations) {
        val currentSelected = selectedLocation
        if (currentSelected != null && businessLocations.none { it.business.getId() == currentSelected.business.getId() }) {
            selectedLocation = null
        }
        if (businessLocations.isEmpty()) {
            mapState = null
        }
    }

    LaunchedEffect(shouldFitBounds, businessLocations, mapSize) {
        if (!shouldFitBounds) return@LaunchedEffect
        if (mapSize == IntSize.Zero || businessLocations.isEmpty()) return@LaunchedEffect
        mapState = businessLocations.toInitialMapState(mapSize)
        shouldFitBounds = false
    }

    LaunchedEffect(retainState) {
        if (!retainState) {
            uiState.businessUiState.updateBusinessMapCameraPosition(null)
            uiState.businessUiState.updateBusinessMapSelectedBusinessId(null)
        }
    }

    LaunchedEffect(retainState) {
        if (!retainState) return@LaunchedEffect
        snapshotFlow {
            val state = mapState
            if (state == null || mapSize == IntSize.Zero) {
                null
            } else {
                state to mapSize
            }
        }
            .filterNotNull()
            .distinctUntilChanged { old, new ->
                old.first == new.first && old.second == new.second
            }
            .collect { (state, size) ->
                val camera = state.toCameraPosition(size)
                uiStateState.value.businessUiState.updateBusinessMapCameraPosition(camera)
            }
    }

    LaunchedEffect(retainState, selectedLocation) {
        if (!retainState) return@LaunchedEffect
        uiState.businessUiState.updateBusinessMapSelectedBusinessId(selectedLocation?.business?.getId())
    }

    DisposableEffect(Unit) {
        onDispose { onInteractionChanged(false) }
    }

    val currentMapState = mapState
    if (currentMapState == null) {
        Box(modifier = modifier.onGloballyPositioned { coordinates -> mapSize = coordinates.size })
        return
    }

    val visibleTiles by remember(currentMapState, mapSize) {
        derivedStateOf { computeVisibleTiles(currentMapState, mapSize) }
    }

    LaunchedEffect(visibleTiles) {
        visibleTiles.forEach { key ->
            if (!tileCache.containsKey(key) && tileLoading[key] != true) {
                tileLoading[key] = true
                launch {
                    try {
                        val bitmap = loadTileBitmap(key)
                        if (bitmap != null) {
                            tileCache[key] = bitmap
                        }
                    } finally {
                        tileLoading.remove(key)
                    }
                }
            }
        }
    }

    var interactionJob by remember { mutableStateOf<Job?>(null) }
    var isInteracting by remember { mutableStateOf(false) }

    fun markInteraction() {
        if (!isInteracting) {
            isInteracting = true
            onInteractionChanged(true)
        }
        interactionJob?.cancel()
        interactionJob = coroutineScope.launch {
            delay(INTERACTION_RESET_DELAY_MS)
            if (isInteracting) {
                isInteracting = false
                onInteractionChanged(false)
            }
        }
    }

    val renderableLocations by remember(currentMapState, mapSize, businessLocations, selectedLocation, density) {
        derivedStateOf {
            val state = currentMapState ?: return@derivedStateOf emptyList()
            calculateRenderableLocations(
                mapState = state,
                allLocations = businessLocations,
                mapSize = mapSize,
                density = density,
                selectedLocation = selectedLocation
            )
        }
    }

    val selectedScreenOffset by remember(currentMapState, mapSize, selectedLocation, renderableLocations) {
        derivedStateOf {
            val state = currentMapState ?: return@derivedStateOf null
            val selected = selectedLocation ?: return@derivedStateOf null
            val selectedId = selected.business.getId()
            if (renderableLocations.none { it.business.getId() == selectedId }) {
                return@derivedStateOf null
            }
            selected.screenOffset(state, mapSize)
        }
    }

    Box(
        modifier = modifier
            .onGloballyPositioned { coordinates ->
                mapSize = coordinates.size
            }
            .pointerInput(currentMapState, mapSize) {
                if (mapSize == IntSize.Zero) return@pointerInput
                var accumulatedScale = 1f
                detectTransformGestures { centroid, pan, zoomChange, _ ->
                    var updatedState = mapState
                    if (updatedState != null) {
                        if (pan != Offset.Zero) {
                            updatedState = updatedState.panBy(pan)
                        }
                        accumulatedScale *= zoomChange
                        if (accumulatedScale > 1.2f) {
                            updatedState = updatedState?.zoomBy(centroid, mapSize, +1)
                            accumulatedScale = 1f
                        } else if (accumulatedScale < 0.8f) {
                            updatedState = updatedState?.zoomBy(centroid, mapSize, -1)
                            accumulatedScale = 1f
                        }
                        if (updatedState != null && updatedState != mapState) {
                            mapState = updatedState
                            shouldFitBounds = false
                            markInteraction()
                        }
                    }
                }
            }
            .pointerInput(currentMapState, renderableLocations, mapSize) {
                if (mapSize == IntSize.Zero) return@pointerInput
                detectTapGestures(onTap = { offset ->
                    val nearest = renderableLocations
                        .mapNotNull { location ->
                            val screen = location.screenOffset(currentMapState, mapSize)
                            if (screen != null) screen to location else null
                        }
                        .map { (screen, location) ->
                            val distance = screen.distanceTo(offset)
                            Triple(distance, screen, location)
                        }
                        .filter { it.first <= tapTargetRadiusPx }
                        .minByOrNull { it.first }

                    if (nearest != null) {
                        val tappedLocation = nearest.third
                        selectedLocation = if (selectedLocation?.business?.getId() == tappedLocation.business.getId()) {
                            null
                        } else {
                            tappedLocation
                        }
                    } else {
                        selectedLocation = null
                    }
                    shouldFitBounds = false
                    markInteraction()
                })
            }
    ) {
        Canvas(
            modifier = Modifier
                .fillMaxSize()
                .background(Color(DEFAULT_MAP_BACKGROUND))
        ) {
            val tiles = visibleTiles
            val scale = TILE_SIZE * 2.0.pow(currentMapState.zoom.toDouble())
            val centerPixelX = currentMapState.centerWorldX * scale
            val centerPixelY = currentMapState.centerWorldY * scale
            val canvasWidth = size.width.toDouble()
            val canvasHeight = size.height.toDouble()
            val halfWidth = canvasWidth / 2.0
            val halfHeight = canvasHeight / 2.0

            tiles.forEach { key ->
                val tileBitmap = tileCache[key]
                val tileWidth = TILE_SIZE
                val tileHeight = TILE_SIZE
                val xOffset = key.x * tileWidth - (centerPixelX - halfWidth)
                val yOffset = key.y * tileHeight - (centerPixelY - halfHeight)
                if (xOffset > canvasWidth || yOffset > canvasHeight || xOffset + tileWidth < -tileWidth || yOffset + tileHeight < -tileHeight) {
                    return@forEach
                }
                val drawOffset = Offset(xOffset.toFloat(), yOffset.toFloat())
                val drawSize = Size(tileWidth.toFloat(), tileHeight.toFloat())
                if (tileBitmap != null) {
                    drawImage(tileBitmap, topLeft = drawOffset)
                } else {
                    drawRect(
                        color = Color(DEFAULT_TILE_BACKGROUND),
                        topLeft = drawOffset,
                        size = drawSize
                    )
                }
            }

            if (enableUserLocation) {
                val location = userLocation
                if (location != null) {
                    val userOffset = location.toScreenOffset(currentMapState, mapSize)
                    if (userOffset != null) {
                        drawCircle(
                            color = Color(0xFF1E88E5),
                            radius = selectedMarkerRadiusPx,
                            center = userOffset
                        )
                        drawCircle(
                            color = Color(0xAA1E88E5),
                            radius = selectedMarkerRadiusPx * 2,
                            center = userOffset
                        )
                    }
                }
            }

            renderableLocations.forEach { location ->
                val offset = location.screenOffset(currentMapState, mapSize) ?: return@forEach
                val isSelected = selectedLocation?.business?.getId() == location.business.getId()
                val radius = if (isSelected) selectedMarkerRadiusPx else markerRadiusPx
                val color = if (isSelected) Color(0xFFE53935) else primary
                drawCircle(color = color, radius = radius, center = offset)
                drawCircle(color = white, radius = radius / 2.5f, center = offset)
            }
        }

        renderableLocations.forEach { location ->
            val offset = location.screenOffset(currentMapState, mapSize) ?: return@forEach
            val businessForLabel = fetchedBusinesses[location.business.getId()] ?: location.business
            val businessName = businessForLabel.getName().ifBlank { null } ?: return@forEach
            val isSelected = selectedLocation?.business?.getId() == location.business.getId()
            val markerRadius = if (isSelected) selectedMarkerRadiusPx else markerRadiusPx
            val labelBackground = if (isSelected) primary else white.copy(alpha = 0.92f)
            val labelTextColor = if (isSelected) white else Color(0xFF1A1A1A)

            Column(
                modifier = Modifier
                    .align(Alignment.TopStart)
                    .offset {
                        IntOffset(
                            (offset.x - markerLabelWidthPx / 2f).roundToInt(),
                            (offset.y + markerRadius + markerLabelSpacingPx).roundToInt()
                        )
                    },
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                Surface(
                    shape = RoundedCornerShape(8.dp),
                    color = labelBackground,
                    elevation = 4.dp
                ) {
                    Text(
                        text = businessName,
                        modifier = Modifier
                            .width(MARKER_LABEL_WIDTH_DP.dp)
                            .padding(horizontal = 8.dp, vertical = 4.dp),
                        fontSize = 12.sp,
                        fontWeight = if (isSelected) FontWeight.Bold else FontWeight.SemiBold,
                        textAlign = TextAlign.Center,
                        color = labelTextColor,
                        maxLines = 2,
                        overflow = TextOverflow.Ellipsis
                    )
                }
            }
        }

        if (selectedLocation != null && selectedScreenOffset != null) {
            val popupAnchorSpacing = anchorOffsetPx + selectedMarkerRadiusPx
            AnchoredPopup(
                modifier = Modifier
                    .align(Alignment.TopStart)
                    .fillMaxSize(),
                mapSize = mapSize,
                anchor = selectedScreenOffset!!,
                anchorPadding = popupAnchorSpacing,
                content = {
                    MapBusinessPopup(
                        uiState = uiState,
                        onTriggerEvent = onTriggerEvent,
                        business = selectedLocation!!.business,
                        onDismiss = {
                            selectedLocation = null
                        }
                    )
                }
            )
        }

        Column(
            modifier = Modifier
                .align(Alignment.TopEnd)
                .padding(12.dp),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            MapControlButton(
                enabled = currentMapState.zoom < MAX_ZOOM_LEVEL,
                onClick = {
                    val newState = mapState?.zoomBy(Offset(mapSize.width / 2f, mapSize.height / 2f), mapSize, +1)
                    if (newState != null) {
                        mapState = newState
                        shouldFitBounds = false
                        markInteraction()
                    }
                }
            ) { isEnabled ->
                MapControlSymbolText("+", isEnabled)
            }
            Spacer(Modifier.height(8.dp))
            MapControlButton(
                enabled = currentMapState.zoom > MIN_ZOOM_LEVEL,
                onClick = {
                    val newState = mapState?.zoomBy(Offset(mapSize.width / 2f, mapSize.height / 2f), mapSize, -1)
                    if (newState != null) {
                        mapState = newState
                        shouldFitBounds = false
                        markInteraction()
                    }
                }
            ) { isEnabled ->
                MapControlSymbolText("−", isEnabled)
            }
            Spacer(Modifier.height(12.dp))
            MapControlButton(
                enabled = true,
                onClick = {
                    val newState = mapState?.panBy(Offset(0f, panStepPx))
                    if (newState != null) {
                        mapState = newState
                        shouldFitBounds = false
                        markInteraction()
                    }
                }
            ) { isEnabled ->
                MapControlArrow(direction = MapArrowDirection.Up, enabled = isEnabled)
            }
            Spacer(Modifier.height(6.dp))
            Row(
                horizontalArrangement = Arrangement.spacedBy(6.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                MapControlButton(
                    enabled = true,
                    onClick = {
                        val newState = mapState?.panBy(Offset(panStepPx, 0f))
                        if (newState != null) {
                            mapState = newState
                            shouldFitBounds = false
                            markInteraction()
                        }
                    }
                ) { isEnabled ->
                    MapControlArrow(direction = MapArrowDirection.Left, enabled = isEnabled)
                }
                MapControlButton(
                    enabled = true,
                    onClick = {
                        val newState = mapState?.panBy(Offset(-panStepPx, 0f))
                        if (newState != null) {
                            mapState = newState
                            shouldFitBounds = false
                            markInteraction()
                        }
                    }
                ) { isEnabled ->
                    MapControlArrow(direction = MapArrowDirection.Right, enabled = isEnabled)
                }
            }
            Spacer(Modifier.height(6.dp))
            MapControlButton(
                enabled = true,
                onClick = {
                    val newState = mapState?.panBy(Offset(0f, -panStepPx))
                    if (newState != null) {
                        mapState = newState
                        shouldFitBounds = false
                        markInteraction()
                    }
                }
            ) { isEnabled ->
                MapControlArrow(direction = MapArrowDirection.Down, enabled = isEnabled)
            }
        }

        if (enableUserLocation && userLocation != null) {
            MapControlButton(
                enabled = true,
                modifier = Modifier
                    .align(Alignment.TopStart)
                    .padding(12.dp),
                onClick = {
                    val state = mapState
                    val location = userLocation
                    if (state != null && location != null) {
                        mapState = state.centerOn(location, max(state.zoom, USER_LOCATION_ZOOM))
                        hasCenteredOnUser = true
                        shouldFitBounds = false
                        markInteraction()
                    }
                }
            ) { isEnabled ->
                MapControlSymbolText("◎", isEnabled)
            }
        }

        MapAttribution(
            modifier = Modifier
                .align(Alignment.BottomStart)
                .padding(12.dp)
        )
    }
}

@Composable
private fun MapControlButton(
    enabled: Boolean,
    modifier: Modifier = Modifier,
    onClick: () -> Unit,
    content: @Composable (Boolean) -> Unit,
) {
    Surface(
        modifier = modifier
            .size(44.dp)
            .clickable(enabled = enabled, onClick = onClick),
        shape = CircleShape,
        color = white.copy(alpha = if (enabled) 0.95f else 0.65f),
        elevation = 6.dp
    ) {
        Box(
            modifier = Modifier.fillMaxSize(),
            contentAlignment = Alignment.Center
        ) {
            content(enabled)
        }
    }
}

@Composable
private fun MapAttribution(modifier: Modifier = Modifier) {
    Surface(
        modifier = modifier,
        shape = RoundedCornerShape(8.dp),
        color = white.copy(alpha = 0.92f),
        elevation = 4.dp
    ) {
        Row(
            modifier = Modifier.padding(horizontal = 12.dp, vertical = 6.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Text(
                text = "© ",
                fontSize = 10.sp,
                color = Color(0xFF424242)
            )
            Text(
                text = "OpenStreetMap contributors",
                fontSize = 10.sp,
                color = primary,
                textDecoration = TextDecoration.Underline,
                modifier = Modifier.clickable {
                    window.open("https://www.openstreetmap.org/copyright", "_blank")
                }
            )
            Text(
                text = " (",
                fontSize = 10.sp,
                color = Color(0xFF424242)
            )
            Text(
                text = "ODbL",
                fontSize = 10.sp,
                color = primary,
                textDecoration = TextDecoration.Underline,
                modifier = Modifier.clickable {
                    window.open("https://opendatacommons.org/licenses/odbl/", "_blank")
                }
            )
            Text(
                text = ")",
                fontSize = 10.sp,
                color = Color(0xFF424242)
            )
        }
    }
}

@Composable
private fun MapControlSymbolText(symbol: String, enabled: Boolean) {
    Text(
        text = symbol,
        color = if (enabled) primary else Color(0xFF9E9E9E),
        fontSize = 18.sp,
        fontWeight = FontWeight.Bold,
        textAlign = TextAlign.Center
    )
}

private enum class MapArrowDirection(val rotation: Float) {
    Up(0f),
    Right(90f),
    Down(180f),
    Left(270f),
}

@Composable
private fun MapControlArrow(direction: MapArrowDirection, enabled: Boolean) {
    val arrowColor = if (enabled) primary else Color(0xFFBDBDBD)
    Canvas(modifier = Modifier.size(18.dp)) {
        val path = Path().apply {
            moveTo(size.width / 2f, size.height * 0.2f)
            lineTo(size.width * 0.75f, size.height * 0.8f)
            lineTo(size.width * 0.25f, size.height * 0.8f)
            close()
        }
        rotate(direction.rotation, pivot = center) {
            drawPath(path = path, color = arrowColor)
        }
    }
}

@Composable
private fun AnchoredPopup(
    modifier: Modifier = Modifier,
    mapSize: IntSize,
    anchor: Offset,
    anchorPadding: Float,
    content: @Composable () -> Unit,
) {
    if (mapSize == IntSize.Zero) return

    SubcomposeLayout(modifier = modifier) { constraints ->
        val childConstraints = constraints.copy(minWidth = 0, minHeight = 0)
        val placeables = subcompose("popup", content).map { it.measure(childConstraints) }
        val targetWidth = if (constraints.maxWidth == Constraints.Infinity) mapSize.width else constraints.maxWidth
        val targetHeight = if (constraints.maxHeight == Constraints.Infinity) mapSize.height else constraints.maxHeight
        val layoutWidth = targetWidth.coerceAtLeast(constraints.minWidth)
        val layoutHeight = targetHeight.coerceAtLeast(constraints.minHeight)

        layout(layoutWidth, layoutHeight) {
            placeables.forEach { placeable ->
                val x = (anchor.x - placeable.width / 2f).roundToInt()
                val y = (anchor.y - anchorPadding - placeable.height).roundToInt()
                placeable.placeRelative(x, y)
            }
        }
    }
}

private fun calculateRenderableLocations(
    mapState: JsMapState,
    allLocations: List<BusinessLocationPin>,
    mapSize: IntSize,
    density: Density,
    selectedLocation: BusinessLocationPin?,
): List<BusinessLocationPin> {
    if (allLocations.isEmpty()) return emptyList()

    val selectedId = selectedLocation?.business?.getId()
    val resolvedSelected = selectedId?.let { id ->
        allLocations.firstOrNull { it.business.getId() == id }
    }
    val prioritizedLocations = buildList {
        resolvedSelected?.let { add(it) }
        allLocations.forEach { location ->
            if (location.business.getId() != selectedId) {
                add(location)
            }
        }
    }

    if (mapSize.width <= 0 || mapSize.height <= 0) {
        return when {
            resolvedSelected != null -> listOf(resolvedSelected)
            prioritizedLocations.isNotEmpty() -> prioritizedLocations.take(MAX_LOW_ZOOM_MARKERS)
            else -> emptyList()
        }
    }

    val zoom = mapState.zoom.toFloat()
    val spacingProgress = when {
        zoom <= MINIMUM_ZOOM_FOR_CLEARANCE -> 0f
        zoom >= MINIMUM_MARKER_ZOOM -> 1f
        else -> ((zoom - MINIMUM_ZOOM_FOR_CLEARANCE) / (MINIMUM_MARKER_ZOOM - MINIMUM_ZOOM_FOR_CLEARANCE)).coerceIn(0f, 1f)
    }

    val horizontalThresholdPx = with(density) {
        lerp(MIN_HORIZONTAL_CLEARANCE_DP, MARKER_HORIZONTAL_CLEARANCE_DP, spacingProgress).dp.toPx()
    }
    val verticalThresholdPx = with(density) {
        lerp(MIN_VERTICAL_CLEARANCE_DP, MARKER_VERTICAL_CLEARANCE_DP, spacingProgress).dp.toPx()
    }

    val acceptedPins = mutableListOf<BusinessLocationPin>()
    val occupiedPoints = mutableListOf<Pair<BusinessLocationPin, Offset>>()
    val centerPoint = Offset(mapSize.width / 2f, mapSize.height / 2f)

    prioritizedLocations.forEach { location ->
        val point = location.screenOffset(mapState, mapSize) ?: return@forEach
        val isSelected = selectedId != null && location.business.getId() == selectedId

        val overlappingEntries = occupiedPoints.filter { (_, existingPoint) ->
            val dx = abs(point.x - existingPoint.x)
            val dy = abs(point.y - existingPoint.y)
            dx < horizontalThresholdPx && dy < verticalThresholdPx
        }

        when {
            overlappingEntries.isEmpty() -> {
                acceptedPins += location
                occupiedPoints += location to point
            }

            isSelected -> {
                overlappingEntries.forEach { entry ->
                    acceptedPins.remove(entry.first)
                    occupiedPoints.remove(entry)
                }
                acceptedPins += location
                occupiedPoints += location to point
            }
        }
    }

    if (acceptedPins.isEmpty()) {
        resolvedSelected?.let { return listOf(it) }
        prioritizedLocations.firstOrNull()?.let { return listOf(it) }
        return emptyList()
    }

    val limitedPins = if (zoom < MINIMUM_MARKER_ZOOM && acceptedPins.size > MAX_LOW_ZOOM_MARKERS) {
        val prioritized = LinkedHashSet<BusinessLocationPin>()
        resolvedSelected?.let { prioritized += it }
        occupiedPoints
            .filter { (location, _) -> acceptedPins.contains(location) }
            .sortedBy { (_, point) -> distanceSquared(point, centerPoint) }
            .forEach { (location, _) ->
                if (prioritized.size < MAX_LOW_ZOOM_MARKERS) {
                    prioritized += location
                }
            }
        prioritized.toList()
    } else {
        acceptedPins
    }

    if (limitedPins.isEmpty()) {
        resolvedSelected?.let { return listOf(it) }
        prioritizedLocations.firstOrNull()?.let { return listOf(it) }
    }

    return limitedPins
}

private suspend fun loadTileBitmap(key: TileKey): ImageBitmap? {
    return try {
        val url = "https://tile.openstreetmap.org/${key.zoom}/${key.x}/${key.y}.png"
        val response = window.fetch(url).await<Response>()
        if (!response.ok) return null
        val buffer = response.arrayBuffer().await()
        val bytes = Int8Array(buffer).unsafeCast<ByteArray>()
        Image.makeFromEncoded(bytes).toComposeImageBitmap()
    } catch (_: Throwable) {
        null
    }
}

private fun BusinessLocationPin.screenOffset(state: JsMapState, size: IntSize): Offset? {
    return toScreenOffset(state, size)
}

private fun BusinessLocationPin.toScreenOffset(state: JsMapState, size: IntSize): Offset? {
    return locationToScreen(latitude, longitude, state, size)
}

private fun Location.toScreenOffset(state: JsMapState, size: IntSize): Offset? {
    return locationToScreen(latitude ?: return null, longitude ?: return null, state, size)
}

private fun Location.hasValidCoordinates(): Boolean {
    return latitude != null && longitude != null
}

private fun locationToScreen(lat: Double, lng: Double, state: JsMapState, size: IntSize): Offset? {
    if (size == IntSize.Zero) return null
    val world = latLngToWorld(lat, lng)
    val adjustedX = adjustWorldX(world.first, state.centerWorldX)
    val scale = TILE_SIZE * 2.0.pow(state.zoom.toDouble())
    val centerPixelX = state.centerWorldX * scale
    val centerPixelY = state.centerWorldY * scale
    val pixelX = adjustedX * scale
    val pixelY = world.second * scale
    val screenX = (pixelX - centerPixelX) + size.width / 2.0
    val screenY = (pixelY - centerPixelY) + size.height / 2.0
    if (screenX < -64.0 || screenY < -64.0 || screenX > size.width + 64.0 || screenY > size.height + 64.0) {
        return null
    }
    return Offset(screenX.toFloat(), screenY.toFloat())
}

private fun adjustWorldX(x: Double, center: Double): Double {
    var adjusted = x
    var diff = adjusted - center
    if (diff > 0.5) {
        adjusted -= 1.0
    } else if (diff < -0.5) {
        adjusted += 1.0
    }
    return adjusted
}

private fun computeVisibleTiles(state: JsMapState, size: IntSize): Set<TileKey> {
    if (size == IntSize.Zero) return emptySet()
    val zoom = state.zoom.coerceIn(MIN_ZOOM_LEVEL, MAX_ZOOM_LEVEL)
    val scale = TILE_SIZE * 2.0.pow(zoom.toDouble())
    val tilesPerAxis = 1 shl zoom
    val centerPixelX = state.centerWorldX * scale
    val centerPixelY = state.centerWorldY * scale
    val halfWidth = size.width / 2.0
    val halfHeight = size.height / 2.0

    val startTileX = floor((centerPixelX - halfWidth) / TILE_SIZE).toInt()
    val endTileX = floor((centerPixelX + halfWidth) / TILE_SIZE).toInt()
    val startTileY = floor((centerPixelY - halfHeight) / TILE_SIZE).toInt()
    val endTileY = floor((centerPixelY + halfHeight) / TILE_SIZE).toInt()

    val keys = LinkedHashSet<TileKey>()
    for (x in startTileX..endTileX) {
        val wrappedX = ((x % tilesPerAxis) + tilesPerAxis) % tilesPerAxis
        for (y in startTileY..endTileY) {
            if (y in 0 until tilesPerAxis) {
                keys += TileKey(zoom = zoom, x = wrappedX, y = y)
            }
        }
    }
    return keys
}

private fun distanceSquared(first: Offset, second: Offset): Float {
    val dx = first.x - second.x
    val dy = first.y - second.y
    return dx * dx + dy * dy
}

private fun lerp(start: Float, end: Float, fraction: Float): Float = start + (end - start) * fraction

private fun JsMapState.panBy(pan: Offset): JsMapState {
    if (pan == Offset.Zero) return this
    val scale = TILE_SIZE * 2.0.pow(zoom.toDouble())
    val dx = pan.x.toDouble() / scale
    val dy = pan.y.toDouble() / scale
    val newX = normalizeWorldX(centerWorldX - dx)
    val newY = clampWorldY(centerWorldY - dy)
    return copy(centerWorldX = newX, centerWorldY = newY)
}

private fun JsMapState.zoomBy(focus: Offset, size: IntSize, delta: Int): JsMapState {
    if (delta == 0) return this
    val targetZoom = (zoom + delta).coerceIn(MIN_ZOOM_LEVEL, MAX_ZOOM_LEVEL)
    if (targetZoom == zoom) return this
    if (size == IntSize.Zero) return this
    val scale = TILE_SIZE * 2.0.pow(zoom.toDouble())
    val newScale = TILE_SIZE * 2.0.pow(targetZoom.toDouble())
    val (focusWorldX, focusWorldY) = screenToWorld(focus, size)
    val centerPixelX = centerWorldX * scale
    val centerPixelY = centerWorldY * scale
    val focusPixelX = focusWorldX * scale
    val focusPixelY = focusWorldY * scale
    val focusPixelXNew = focusWorldX * newScale
    val focusPixelYNew = focusWorldY * newScale
    val newCenterPixelX = centerPixelX + (focusPixelXNew - focusPixelX)
    val newCenterPixelY = centerPixelY + (focusPixelYNew - focusPixelY)
    val newCenterWorldX = normalizeWorldX(newCenterPixelX / newScale)
    val newCenterWorldY = clampWorldY(newCenterPixelY / newScale)
    return copy(centerWorldX = newCenterWorldX, centerWorldY = newCenterWorldY, zoom = targetZoom)
}

private fun JsMapState.screenToWorld(point: Offset, size: IntSize): Pair<Double, Double> {
    val scale = TILE_SIZE * 2.0.pow(zoom.toDouble())
    val topLeftPixelX = centerWorldX * scale - size.width / 2.0
    val topLeftPixelY = centerWorldY * scale - size.height / 2.0
    val worldX = (point.x.toDouble() + topLeftPixelX) / scale
    val worldY = (point.y.toDouble() + topLeftPixelY) / scale
    return normalizeWorldX(worldX) to clampWorldY(worldY)
}

private fun JsMapState.centerOn(location: Location, zoomLevel: Int): JsMapState {
    val lat = location.latitude ?: return this
    val lng = location.longitude ?: return this
    val centerWorld = latLngToWorld(lat, lng)
    return copy(
        centerWorldX = normalizeWorldX(centerWorld.first),
        centerWorldY = clampWorldY(centerWorld.second),
        zoom = zoomLevel.coerceIn(MIN_ZOOM_LEVEL, MAX_ZOOM_LEVEL)
    )
}

private fun BusinessMapCameraPosition.toJsMapState(size: IntSize): JsMapState {
    val centerWorld = latLngToWorld(latitude, longitude)
    val zoom = estimateZoomForBounds(
        latitude - latitudeDelta / 2,
        latitude + latitudeDelta / 2,
        longitude - longitudeDelta / 2,
        longitude + longitudeDelta / 2,
        size
    )
    return JsMapState(
        centerWorldX = normalizeWorldX(centerWorld.first),
        centerWorldY = clampWorldY(centerWorld.second),
        zoom = zoom
    )
}

private fun List<BusinessLocationPin>.toInitialMapState(size: IntSize): JsMapState {
    if (isEmpty()) {
        return JsMapState(centerWorldX = 0.5, centerWorldY = 0.5, zoom = DEFAULT_ZOOM_LEVEL)
    }
    if (size == IntSize.Zero) {
        val first = first()
        val centerWorld = latLngToWorld(first.latitude, first.longitude)
        return JsMapState(centerWorld.first, centerWorld.second, DEFAULT_ZOOM_LEVEL)
    }

    val worldPoints = map { latLngToWorld(it.latitude, it.longitude) }
    var minX = worldPoints.minOf { it.first }
    var maxX = worldPoints.maxOf { it.first }
    if (maxX - minX > 0.5) {
        val adjusted = worldPoints.map { if (it.first < 0.5) it.first + 1.0 else it.first }
        minX = adjusted.minOf { it }
        maxX = adjusted.maxOf { it }
    }
    val minY = worldPoints.minOf { it.second }
    val maxY = worldPoints.maxOf { it.second }

    val centerX = ((minX + maxX) / 2.0).let { if (it > 1.0) it - 1.0 else it }
    val centerY = (minY + maxY) / 2.0

    val spanLatSouth = worldToLatLng(centerX, maxY).first
    val spanLatNorth = worldToLatLng(centerX, minY).first
    val spanLonWest = worldToLatLng(minX, centerY).second
    val spanLonEast = worldToLatLng(maxX, centerY).second

    val zoom = estimateZoomForBounds(spanLatSouth, spanLatNorth, spanLonWest, spanLonEast, size)

    return JsMapState(
        centerWorldX = normalizeWorldX(centerX),
        centerWorldY = clampWorldY(centerY),
        zoom = zoom
    )
}

private fun JsMapState.toCameraPosition(size: IntSize): BusinessMapCameraPosition {
    if (size == IntSize.Zero) {
        val center = worldToLatLng(centerWorldX, centerWorldY)
        return BusinessMapCameraPosition(
            latitude = center.first,
            longitude = center.second,
            latitudeDelta = MIN_SPAN,
            longitudeDelta = MIN_SPAN
        )
    }
    val scale = TILE_SIZE * 2.0.pow(zoom.toDouble())
    val halfWidth = size.width / 2.0
    val halfHeight = size.height / 2.0
    val topLeftX = (centerWorldX * scale - halfWidth) / scale
    val topLeftY = (centerWorldY * scale - halfHeight) / scale
    val bottomRightX = (centerWorldX * scale + halfWidth) / scale
    val bottomRightY = (centerWorldY * scale + halfHeight) / scale

    val topLeftLatLng = worldToLatLng(normalizeWorldX(topLeftX), clampWorldY(topLeftY))
    val bottomRightLatLng = worldToLatLng(normalizeWorldX(bottomRightX), clampWorldY(bottomRightY))

    val centerLatLng = worldToLatLng(centerWorldX, centerWorldY)

    val latDelta = abs(topLeftLatLng.first - bottomRightLatLng.first).coerceAtLeast(MIN_SPAN)
    val lonDeltaRaw = abs(topLeftLatLng.second - bottomRightLatLng.second)
    val lonDelta = if (lonDeltaRaw > 180.0) 360.0 - lonDeltaRaw else lonDeltaRaw

    return BusinessMapCameraPosition(
        latitude = centerLatLng.first,
        longitude = centerLatLng.second,
        latitudeDelta = latDelta,
        longitudeDelta = lonDelta.coerceAtLeast(MIN_SPAN)
    )
}

private fun estimateZoomForBounds(
    southLat: Double,
    northLat: Double,
    westLng: Double,
    eastLng: Double,
    size: IntSize,
): Int {
    val clampedNorth = northLat.coerceAtMost(MAX_LATITUDE)
    val clampedSouth = southLat.coerceAtLeast(MIN_LATITUDE)
    val worldNorth = latLngToWorld(clampedNorth, 0.0).second
    val worldSouth = latLngToWorld(clampedSouth, 0.0).second
    val worldSpanY = (worldSouth - worldNorth).coerceAtLeast(MIN_SPAN)

    val normalizedWest = wrapLongitude(westLng)
    val normalizedEast = wrapLongitude(eastLng)
    var worldSpanX = latLngToWorld(0.0, normalizedEast).first - latLngToWorld(0.0, normalizedWest).first
    if (worldSpanX < 0) {
        worldSpanX += 1.0
    }
    worldSpanX = worldSpanX.coerceAtLeast(MIN_SPAN)

    val paddedX = worldSpanX * BOUNDS_PADDING_FACTOR
    val paddedY = worldSpanY * BOUNDS_PADDING_FACTOR

    val zoomX = ln(size.width / (TILE_SIZE * paddedX)) / ln(2.0)
    val zoomY = ln(size.height / (TILE_SIZE * paddedY)) / ln(2.0)
    val zoom = floor(min(zoomX, zoomY)).toInt()
    return zoom.coerceIn(MIN_ZOOM_LEVEL, MAX_ZOOM_LEVEL)
}

private fun normalizeWorldX(value: Double): Double {
    var v = value % 1.0
    if (v < 0) v += 1.0
    return v
}

private fun clampWorldY(value: Double): Double {
    val minY = latLngToWorld(MAX_LATITUDE, 0.0).second
    val maxY = latLngToWorld(MIN_LATITUDE, 0.0).second
    return value.coerceIn(minY, maxY)
}

private fun wrapLongitude(longitude: Double): Double {
    var value = longitude
    while (value < -180.0) value += 360.0
    while (value > 180.0) value -= 360.0
    return value
}

private fun latLngToWorld(latitude: Double, longitude: Double): Pair<Double, Double> {
    val clampedLat = latitude.coerceIn(MIN_LATITUDE, MAX_LATITUDE)
    val sinLat = sin(clampedLat * PI / 180.0)
    val x = (longitude + 180.0) / 360.0
    val y = 0.5 - ln((1 + sinLat) / (1 - sinLat)) / (4 * PI)
    return Pair(normalizeWorldX(x), y)
}

private fun worldToLatLng(worldX: Double, worldY: Double): Pair<Double, Double> {
    val lon = worldX * 360.0 - 180.0
    val n = PI - 2.0 * PI * worldY
    val lat = 180.0 / PI * atan(0.5 * (kotlin.math.exp(n) - kotlin.math.exp(-n)))
    return Pair(lat, lon)
}

private fun Offset.distanceTo(other: Offset): Float {
    val dx = x - other.x
    val dy = y - other.y
    return sqrt(dx * dx + dy * dy)
}

private const val MIN_LATITUDE = -85.0
private const val MAX_LATITUDE = 85.0
